import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { 
  onAuthStateChanged, 
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  updateProfile
} from 'firebase/auth';
import { auth, signInWithGooglePopup, signInWithApplePopup, signOutUser } from '../config/firebase';
import { apiCall, API_ROUTES } from '../config/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const initialUser = auth.currentUser;
  const [currentUser, setCurrentUser] = useState(initialUser);
  const [loading, setLoading] = useState(!initialUser);
  const [justSignedIn, setJustSignedIn] = useState(false);
  const previousUserRef = useRef(initialUser);

  // Clone default recording for new users (matching iOS behavior)
  const cloneDefaultRecording = useCallback(async (userId) => {
    try {
      console.log('AuthContext: Cloning default recording for new user:', userId);

      const result = await apiCall(API_ROUTES.CLONE_DEFAULT_RECORDING, {
        method: 'POST'
      });

      console.log('AuthContext: Default recordings cloned successfully:', result);
    } catch (error) {
      console.error('AuthContext: Error cloning default recording:', error);
      // Don't block app loading if cloning fails (matching iOS behavior)
    }
  }, []);

  useEffect(() => {
    let hasResolvedInitialAuthState = false;
    const authBootstrapTimeout = setTimeout(() => {
      if (!hasResolvedInitialAuthState) {
        console.warn('AuthContext: auth bootstrap timed out, continuing with current auth snapshot');
        setCurrentUser(auth.currentUser);
        setLoading(false);
      }
    }, 3000);

    const unsubscribe = onAuthStateChanged(auth, (user) => {
      hasResolvedInitialAuthState = true;
      clearTimeout(authBootstrapTimeout);
      const wasSignedOut = !previousUserRef.current;
      setCurrentUser(user);
      previousUserRef.current = user;
      
      // If user just signed in (was null before, now has user), set the flag
      if (user && wasSignedOut) {
        setJustSignedIn(true);
        console.log('User just signed in, will check for paywall');
      }

      // Check if user is new based on metadata (matching iOS behavior)
      if (user?.metadata?.creationTime) {
        const creationTime = new Date(user.metadata.creationTime).getTime();
        const lastSignInTime = new Date(user.metadata.lastSignInTime).getTime();
        
        // If creation time is close to last sign in time, this is likely a new user
        const isNew = Math.abs(creationTime - lastSignInTime) < 60000; // Within a minute
        console.log('AuthContext: User is new:', isNew);
        
        // Clone default recording for new users (matching iOS behavior)
        if (isNew) {
          cloneDefaultRecording(user.uid);
        }
      }
      
      setLoading(false);
    });

    return () => {
      clearTimeout(authBootstrapTimeout);
      unsubscribe();
    };
  }, [cloneDefaultRecording]);

  // Function to clear the just signed in flag (called after paywall check)
  const clearJustSignedIn = () => {
    setJustSignedIn(false);
  };

  // Register with email and password
  const register = async (email, password, userInfo = {}) => {
    try {
      const result = await createUserWithEmailAndPassword(auth, email, password);
      
      // Update user profile with additional info
      if (userInfo.firstName || userInfo.lastName) {
        const displayName = `${userInfo.firstName || ''} ${userInfo.lastName || ''}`.trim();
        await updateProfile(result.user, {
          displayName: displayName
        });
      }
      
      return result;
    } catch (error) {
      console.error('Error registering user:', error);
      throw error;
    }
  };

  // Login with email and password
  const login = async (email, password) => {
    try {
      const result = await signInWithEmailAndPassword(auth, email, password);
      return result;
    } catch (error) {
      console.error('Error logging in:', error);
      throw error;
    }
  };

  // Sign in with Google
  const signInWithGoogle = async () => {
    try {
      const result = await signInWithGooglePopup();
      // Result includes additionalUserInfo.isNewUser for detecting new users
      return result;
    } catch (error) {
      console.error('Error signing in with Google:', error);
      throw error;
    }
  };

  // Sign in with Apple
  const signInWithApple = async () => {
    try {
      const result = await signInWithApplePopup();
      // Result includes additionalUserInfo.isNewUser for detecting new users
      return result;
    } catch (error) {
      console.error('Error signing in with Apple:', error);
      throw error;
    }
  };

  // Sign out / logout
  const signOut = async () => {
    try {
      await signOutUser();
    } catch (error) {
      console.error('Error signing out:', error);
      throw error;
    }
  };

  // Alias for logout
  const logout = signOut;

  const value = {
    currentUser,
    register,
    login,
    signInWithGoogle,
    signInWithApple,
    signOut,
    logout,
    loading,
    justSignedIn,
    clearJustSignedIn
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
